
import { memo, useMemo } from 'react'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import { CATEGORY_META, SLOT_BUS_LABELS, slotDescriptor } from '@/lib/categories'
import { Icon } from '@/components/ui/Icon'
import { describeDevice, formatFormFactor, SIGNAL_META, type SignalCategory } from '@/lib/signals'
import {
  handleIdFor,
  partitionPorts,
  portGroupName,
  splitPorts,
  type DeviceDTO,
  type DeviceSlotDTO,
  type PortDTO,
} from '@/lib/types'
import { groupKey, useCanvasView } from './CanvasView'
import type { DeviceFlowNode } from './types'

type Group = { name: string; ports: PortDTO[] }

/** Groups a run of ports by name, preserving the order they are laid out in. */
function buildGroups(ports: PortDTO[]): Group[] {
  const groups: Group[] = []
  const index = new Map<string, Group>()
  for (const port of ports) {
    const name = portGroupName(port)
    let group = index.get(name)
    if (!group) {
      group = { name, ports: [] }
      index.set(name, group)
      groups.push(group)
    }
    group.ports.push(port)
  }
  return groups
}

/**
 * Handle positions, built once per signal and side.
 *
 * These were object literals in the row body, so every render handed
 * `memo(Handle)` a new `style` and every handle re-rendered. A fifty-device rig
 * carries on the order of fifteen hundred handles, and the whole lot was
 * re-rendering each time a cable was patched — the stutter right after
 * dropping a connection.
 */
const HANDLE_STYLE: Record<SignalCategory, { left: React.CSSProperties; right: React.CSSProperties }> =
  Object.fromEntries(
    (Object.keys(SIGNAL_META) as SignalCategory[]).map((signal) => [
      signal,
      {
        left: { background: SIGNAL_META[signal].color, left: -6 },
        right: { background: SIGNAL_META[signal].color, right: -6 },
      },
    ]),
  ) as Record<SignalCategory, { left: React.CSSProperties; right: React.CSSProperties }>

const PortRow = memo(function PortRow({ port }: { port: PortDTO }) {
  const color = SIGNAL_META[port.signal].color
  const isInput = port.direction === 'INPUT'
  const isBidi = port.direction === 'BIDIRECTIONAL'
  const title = `${port.name} — ${port.connector} (${SIGNAL_META[port.signal].label}${
    isBidi ? ', bi-directional' : ''
  })`

  if (isBidi) {
    // One physical connector, two handles: it can be fed from the left or
    // feed out to the right, and the canvas decides which when you patch it.
    return (
      <div className="relative flex h-[22px] items-center justify-between px-3" title={title}>
        <Handle
          type="target"
          position={Position.Left}
          id={handleIdFor(port, 'target')}
          style={HANDLE_STYLE[port.signal].left}
        />
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            aria-hidden="true"
            className="h-1.5 w-1.5 shrink-0 rotate-45 border border-[var(--node-surface)]"
            style={{ backgroundColor: color }}
          />
          <span className="truncate text-[10px] leading-none text-foreground/75">{port.name}</span>
        </span>
        <Handle
          type="source"
          position={Position.Right}
          id={handleIdFor(port, 'source')}
          style={HANDLE_STYLE[port.signal].right}
        />
      </div>
    )
  }

  return (
    <div
      className={`relative flex h-[22px] items-center gap-1.5 ${
        isInput ? 'pl-3 pr-1' : 'flex-row-reverse pl-1 pr-3'
      }`}
      title={title}
    >
      <Handle
        type={isInput ? 'target' : 'source'}
        position={isInput ? Position.Left : Position.Right}
        id={port.id}
        style={isInput ? HANDLE_STYLE[port.signal].left : HANDLE_STYLE[port.signal].right}
      />
      <span
        aria-hidden="true"
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className="truncate text-[10px] leading-none text-foreground/75">{port.name}</span>
    </div>
  )
})

function CollapsedRow({
  group,
  align,
  onExpand,
}: {
  group: Group
  align: 'left' | 'right'
  onExpand: () => void
}) {
  const color = SIGNAL_META[group.ports[0].signal].color
  return (
    <button
      type="button"
      onClick={onExpand}
      title={`Show all ${group.ports.length} ${group.name} ports`}
      className={`nodrag flex h-[22px] w-full items-center gap-1.5 rounded-md px-3 text-[10px] leading-none text-muted transition-colors hover:bg-hover hover:text-foreground ${
        align === 'right' ? 'flex-row-reverse text-right' : ''
      }`}
    >
      <span
        aria-hidden="true"
        className="h-1.5 w-1.5 shrink-0 rounded-full opacity-50"
        style={{ backgroundColor: color }}
      />
      <span className="truncate">
        {group.name} ×{group.ports.length}
      </span>
      <span aria-hidden="true" className="ml-auto shrink-0 text-muted/40">
        {align === 'right' ? '‹' : '›'}
      </span>
    </button>
  )
}

function PortSection({
  nodeId,
  scope,
  ports,
  align,
  heading,
}: {
  nodeId: string
  /** 'board' for onboard ports, else the slot id — keeps collapse state per slot. */
  scope: string
  ports: PortDTO[]
  align: 'left' | 'right'
  heading: string
}) {
  const { patchedPortIds, expandedGroups, toggleGroup, collapseThreshold } = useCanvasView()
  const groups = useMemo(() => buildGroups(ports), [ports])

  if (ports.length === 0) return null

  return (
    <div className="flex flex-col gap-px">
      <p
        className={`px-3 pb-0.5 font-mono text-[8px] uppercase tracking-[0.12em] text-muted/70 ${
          align === 'right' ? 'text-right' : ''
        }`}
      >
        {heading}
      </p>
      {groups.map((group) => {
        const key = groupKey(nodeId, scope, heading, group.name)
        const collapsed = group.ports.length >= collapseThreshold && !expandedGroups.has(key)
        if (!collapsed) {
          return group.ports.map((port) => <PortRow key={port.id} port={port} />)
        }
        // Patched ports stay on show even when their run is collapsed, so no
        // cable is ever attached to something you cannot see.
        const patched = group.ports.filter((port) => patchedPortIds.has(port.id))
        return (
          <div key={key} className="flex flex-col gap-px">
            <CollapsedRow group={group} align={align} onExpand={() => toggleGroup(key)} />
            {patched.map((port) => (
              <PortRow key={port.id} port={port} />
            ))}
          </div>
        )
      })}
    </div>
  )
}

type Split = ReturnType<typeof splitPorts>

/** The In/Out grid plus the bi-directional block, for the board or one slot. */
function PortBlock({ nodeId, scope, split }: { nodeId: string; scope: string; split: Split }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-x-1 py-1.5">
        <PortSection nodeId={nodeId} scope={scope} ports={split.inputs} align="left" heading="In" />
        <PortSection nodeId={nodeId} scope={scope} ports={split.outputs} align="right" heading="Out" />
      </div>

      {split.bidirectional.length > 0 && (
        <div className="border-t border-[var(--border)] py-1.5">
          <PortSection
            nodeId={nodeId}
            scope={scope}
            ports={split.bidirectional}
            align="left"
            heading="Bi-directional"
          />
        </div>
      )}
    </>
  )
}

type SlotSection = { slot: DeviceSlotDTO; split: Split; hidden: number }

type NodeSections = {
  onboard: Split
  slots: SlotSection[]
  emptySlots: DeviceSlotDTO[]
  hiddenCount: number
}

/**
 * Partitions a device's ports into what the node draws: the onboard block, one
 * block per slot with a card in it, and the empty slots — each filtered by the
 * signal toggles. Pure so it can be memoised on the device and the filters.
 */
function buildNodeSections(
  device: DeviceDTO,
  visibleSignals: Record<SignalCategory, boolean>,
): NodeSections {
  const { onboard, bySlot } = partitionPorts(device)
  const shown = (ports: PortDTO[]) => ports.filter((port) => visibleSignals[port.signal])

  const onboardShown = shown(onboard)
  let visibleTotal = onboardShown.length
  const slots: SlotSection[] = []
  const emptySlots: DeviceSlotDTO[] = []
  for (const slot of device.slots) {
    if (!slot.card) {
      emptySlots.push(slot)
      continue
    }
    const ports = bySlot.get(slot.id) ?? []
    const visible = shown(ports)
    visibleTotal += visible.length
    slots.push({ slot, split: splitPorts(visible), hidden: ports.length - visible.length })
  }

  return {
    onboard: splitPorts(onboardShown),
    slots,
    emptySlots,
    hiddenCount: device.ports.length - visibleTotal,
  }
}

function DeviceNodeComponent({ id, data, selected }: NodeProps<DeviceFlowNode>) {
  const { device, label } = data
  const { visibleSignals } = useCanvasView()

  const sections = useMemo(() => buildNodeSections(device, visibleSignals), [device, visibleSignals])
  const { hiddenCount } = sections

  return (
    <div
      // No `overflow-hidden`: connection handles deliberately sit outside the
      // node's edge, and clipping them makes cables impossible to pull.
      className={`w-[264px] rounded-2xl border bg-[var(--node-surface)] transition-[box-shadow,border-color] duration-150 ${
        selected
          ? 'border-[var(--accent)] shadow-[0_0_0_3px_var(--ring),var(--shadow-float)]'
          : 'border-[var(--border)] shadow-[var(--shadow-card)]'
      }`}
    >
      <header className="flex items-start gap-2 rounded-t-[15px] border-b border-[var(--border)] bg-gradient-to-br from-[var(--node-header)] to-[var(--node-surface)] px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12px] font-semibold leading-tight text-foreground">
            {label || device.name}
          </p>
          <p className="mt-0.5 truncate text-[10px] leading-tight text-muted">
            {describeDevice(device.manufacturer, device.model)}
          </p>
          {(formatFormFactor(device.formFactorRU) || device.ipAddress) && (
            <p className="mt-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wide text-muted/80">
              {formatFormFactor(device.formFactorRU) && <span>{formatFormFactor(device.formFactorRU)}</span>}
              {formatFormFactor(device.formFactorRU) && device.ipAddress && <span aria-hidden="true">·</span>}
              {device.ipAddress && <span className="truncate">{device.ipAddress}</span>}
            </p>
          )}
        </div>
        {/* The category as a symbol on its own, clear of the text, so a box
            can be told from the next one without reading either. Bare, no
            tile: a symbol, not a button. */}
        <Icon
          name={CATEGORY_META[device.category].icon}
          strokeWidth={2}
          className="mt-0.5 h-[18px] w-[18px] shrink-0 text-foreground/60"
          aria-label={CATEGORY_META[device.category].singular}
        />
      </header>

      <PortBlock nodeId={id} scope="board" split={sections.onboard} />

      {sections.slots.map(({ slot, split, hidden }) => (
        <div key={slot.id} className="border-t border-[var(--border)]">
          {/* A plain div, never a button: nothing on the canvas may carry a
              model name as an accessible button name. */}
          <div
            className="flex items-baseline gap-1.5 bg-[var(--surface-raised)] px-3 py-1"
            title={`${slot.label} · ${slot.card ? describeDevice(slot.card.manufacturer, slot.card.model) : ''}`}
          >
            <span className="truncate text-[10px] font-medium leading-none text-foreground/85">
              {slotDescriptor(slot.label, slot.card?.model)}
            </span>
            <span className="ml-auto shrink-0 font-mono text-[8px] uppercase tracking-[0.12em] text-muted/70">
              {SLOT_BUS_LABELS[slot.bus]}
              {hidden > 0 ? ` · ${hidden} hidden` : ''}
            </span>
          </div>
          <PortBlock nodeId={id} scope={slot.id} split={split} />
        </div>
      ))}

      {sections.emptySlots.length > 0 && (
        <p className="border-t border-[var(--border)] px-3 py-1 font-mono text-[9px] text-muted/70">
          {sections.emptySlots.length} empty slot{sections.emptySlots.length === 1 ? '' : 's'} ·{' '}
          {sections.emptySlots.map((slot) => slot.label).join(', ')}
        </p>
      )}

      {hiddenCount > 0 && (
        <p className="border-t border-[var(--border)] px-3 py-1 font-mono text-[9px] text-muted/70">
          {hiddenCount} port{hiddenCount === 1 ? '' : 's'} hidden by filters
        </p>
      )}
    </div>
  )
}

export const DeviceNode = memo(DeviceNodeComponent)
